import { ReactElement, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import AveragePlots from './AveragePlots';
import LinePlot from './LinePlot';
import { CDA_KEY } from './plotConstants';
import RunMap from '../../../RunView/RunDetails/RunMap';
import { useOutletContext } from 'react-router-dom';
import ChartCard from '../../../../components/ChartCard';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import WeatherTemperaturePlot from '../WeatherTemperaturePlot';
import { HiChevronDown, HiChevronUp, HiOutlineDocumentArrowDown } from 'react-icons/hi2';
import { useRidersById } from '../../../../hooks/useRiders';
import streamlinesLogo from '../../../../icons/logo-streamlines.png';
import { executeExportHtml } from '../../../../utils/printUtils';
import { getDisplayName } from '../../../../types/Rider';
import { parse, isValid } from 'date-fns';

const NUMBERS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];

// Calculate elevation difference between gates A and B
function calculateElevationDifference(route: any, gates: any): number {
  if (!route.route_beta || !route.route_lat || !route.route_lon || !gates.gates_lat || !gates.gates_lon) {
    return 0;
  }

  // Helper to find closest route index to a gate
  const findClosestRouteIndex = (gateLat: number, gateLon: number): number => {
    let minDistance = Infinity;
    let closestIndex = 0;

    for (let i = 0; i < route.route_lat.length; i++) {
      const lat = route.route_lat[i];
      const lon = route.route_lon[i];
      const distance = Math.sqrt(Math.pow(lat - gateLat, 2) + Math.pow(lon - gateLon, 2));
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }
    return closestIndex;
  };

  const gateAIndex = findClosestRouteIndex(gates.gates_lat[0], gates.gates_lon[0]);
  const gateBIndex = findClosestRouteIndex(gates.gates_lat[1], gates.gates_lon[1]);

  const gateABeta = route.route_beta[gateAIndex];
  const gateBBeta = route.route_beta[gateBIndex];

  // Calculate elevation difference: (betaB - betaA) / -9.8
  return (gateBBeta - gateABeta) / -9.8;
}

function ConfigGroup({
  baselineId,
  configGroups,
  configMap,
  displayNames,
}: {
  baselineId: string;
  configGroups: Map<string, any>;
  configMap: Map<string, any>;
  displayNames: string[];
}): ReactElement {
  const baseline = configMap.get(baselineId);
  const configs = configGroups.get(baselineId);
  return (
    <div className="cr-mb-4">
      {!baseline ? (
        <div className="font-montserrat cr-text-lg cr-font-medium cr-mb-2">Baseline Config</div>
      ) : (
        <div className="font-montserrat cr-text-lg cr-font-medium cr-mb-2">Delta to Config {baseline.letter}</div>
      )}
      <div className="cr-rounded-xl cr-shadow-md cr-overflow-hidden">
        <div className="cr-sticky cr-top-0 cr-z-50 cr-grid cr-h-[70px] cr-min-h-[70px] cr-grid-cols-[1fr_3fr_1fr_1fr_1fr_1fr] cr-border-t cr-border-cirrusBlue-100 cr-bg-cirrusBlue-50 cr-text-blue-900 cr-text-lg cr-font-semibold cr-items-center">
          {displayNames.map((name, index) => (
            <div key={name + index} className={name === 'Description' ? 'cr-pl-4 cr-text-left' : 'cr-text-center'}>
              {name}
            </div>
          ))}
        </div>
        {configs.map((config: any) => (
          <Config key={config.id} config={config} baseline={baseline} />
        ))}
      </div>
    </div>
  );
}

function Config({ config, baseline }: { config: any; baseline: any }): ReactElement {
  const baselineCda = baseline?.average[CDA_KEY][0] || null;
  const cda = config.average[CDA_KEY] && config.average[CDA_KEY][0];
  const cdaDiff = baselineCda ? cda - baselineCda : null;
  const powerSpeedKey = 'RiderPowerFromSpeed_PG3';
  const baselinePower = (baseline?.average[powerSpeedKey] && baseline?.average[powerSpeedKey][0]) || null;
  const power = config.average[powerSpeedKey] && config.average[powerSpeedKey][0];
  const powerDiff = baselinePower ? power - baselinePower : null;
  const diffClass = (amount: number | null) =>
    amount && amount > 0 ? 'cr-text-center cr-text-red-600' : 'cr-text-center cr-font-bold cr-text-blue-500';
  const formatNumber = (amount: number | null, places: number) => (amount ? amount.toFixed(places) : '-');
  return (
    <div className="cr-grid cr-grid-cols-[1fr_3fr_1fr_1fr_1fr_1fr] cr-bg-cirrusBlue-25 cr-px-1 cr-py-2 cr-text-lg cr-items-center">
      <div className="cr-text-center">{config.letter}</div>
      <div className="cr-pl-4 cr-text-left">{config.description}</div>
      <div className="cr-text-center">{formatNumber(cda, 3)}</div>
      <div className={diffClass(cdaDiff)}>{formatNumber(cdaDiff, 3)}</div>
      <div className="cr-text-center">{formatNumber(power, 0)}</div>
      <div className={diffClass(powerDiff)}>{formatNumber(powerDiff, 0)}</div>
    </div>
  );
}

function ReportMap({ gate, route }: { gate: any; route: any }): ReactElement {
  return route ? (
    <RunMap
      lats={route.route_lat}
      lons={route.route_lon}
      gates_lat={gate?.gates_lat}
      gates_lon={gate?.gates_lon}
    />
  ) : (
    <div>Loading Map Data...</div>
  );
}

function ElevationPlot({ route, gates }: { route: any; gates: any }): ReactElement {
  if (!route || !route.route_beta || !Array.isArray(route.route_beta)) {
    return <div className="cr-p-4 cr-text-gray-500">No elevation data available</div>;
  }

  // Find gate positions on the route
  const findClosestRouteIndex = (gateLat: number, gateLon: number): number => {
    if (!route.route_lat || !route.route_lon) return -1;

    let minDistance = Infinity;
    let closestIndex = 0;

    for (let i = 0; i < route.route_lat.length; i++) {
      const lat = route.route_lat[i];
      const lon = route.route_lon[i];
      // Simple Euclidean distance (good enough for small distances)
      const distance = Math.sqrt(Math.pow(lat - gateLat, 2) + Math.pow(lon - gateLon, 2));
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }

    return closestIndex;
  };

  // Find Gate A position and get its beta value
  let gateABeta = 0;
  if (gates?.gates_lat && gates?.gates_lon) {
    const gateAIndex = findClosestRouteIndex(gates.gates_lat[0], gates.gates_lon[0]);
    if (gateAIndex >= 0 && gateAIndex < route.route_beta.length) {
      gateABeta = route.route_beta[gateAIndex];
    }
  }

  // Calculate elevation as (route_beta - gateABeta) / -9.8
  const elevationData = route.route_beta.map((beta: number, index: number) => ({
    x: route.route_pos?.[index] ?? index,
    y: (beta - gateABeta) / -9.8,
  }));

  const annotations: any[] = [];
  if (gates?.gates_lat && gates?.gates_lon) {
    const gateAIndex = findClosestRouteIndex(gates.gates_lat[0], gates.gates_lon[0]);
    const gateBIndex = findClosestRouteIndex(gates.gates_lat[1], gates.gates_lon[1]);

    if (gateAIndex >= 0) {
      const xPos = route.route_pos?.[gateAIndex] ?? gateAIndex;
      annotations.push({
        x: xPos,
        borderColor: '#BBD5DE',
        label: {
          borderColor: '#BBD5DE',
          style: {
            color: '#03365D',
            background: '#BBD5DE',
          },
          text: 'Gate A',
        },
      });
    }

    if (gateBIndex >= 0) {
      const xPos = route.route_pos?.[gateBIndex] ?? gateBIndex;
      annotations.push({
        x: xPos,
        borderColor: '#03365D',
        label: {
          borderColor: '#03365D',
          style: {
            color: '#fff',
            background: '#03365D',
          },
          text: 'Gate B',
        },
      });
    }
  }

  const options: ApexOptions = {
    chart: {
      type: 'scatter',
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: true,
      },
    },
    xaxis: {
      title: {
        text: route.route_pos ? 'Position (m)' : 'Index',
      },
      decimalsInFloat: 1,
    },
    yaxis: {
      title: {
        text: 'Elevation (m)',
      },
      decimalsInFloat: 1,
      labels: {
        formatter: (value: number) => value.toFixed(1),
      },
    },
    markers: {
      size: 4,
    },
    colors: ['#1879B9'],
    title: {
      text: 'Route Elevation Profile',
      align: 'center',
    },
    tooltip: {
      y: {
        formatter: (value: number) => value.toFixed(1) + ' m',
      },
    },
    annotations: {
      xaxis: annotations,
    },
  };

  const series = [
    {
      name: 'Elevation',
      data: elevationData,
    },
  ];

  return (
    <ChartCard className="cr-h-96" padding>
      <Chart options={options} series={series} type="scatter" height="100%" />
    </ChartCard>
  );
}

function WindSpeedPlot({ configs, utcOffsetSeconds, weatherControllerDict }: {
  configs: any[];
  utcOffsetSeconds: number;
  weatherControllerDict: any;
}): ReactElement {
  const probeData: any[] = [];

  // Collect average wind speed data from all runs (probe)
  for (const config of configs) {
    for (const run of config.runs) {
      const avgWindSpeed = run.average?.WindAbs_vG3?.[0];
      const runTime = run.average?.GatesStartTs_tG3?.[0];

      if (avgWindSpeed == null || runTime == null) {
        continue;
      }

      probeData.push({
        x: (runTime + utcOffsetSeconds) * 1000, // Convert to milliseconds
        y: avgWindSpeed * 3.6, // Convert m/s to km/h
      });
    }
  }

  // Collect Weather API wind speed data (unscaled initially)
  const apiData: any[] = [];
  const weatherApiSource = weatherControllerDict?.weather_api;
  if (weatherApiSource?.weather_data) {
    const validData = weatherApiSource.weather_data
      .filter((d: any) => d.Wind_v !== null && d.TimeAbs_t !== null)
      .sort((a: any, b: any) => a.TimeAbs_t - b.TimeAbs_t);

    for (const d of validData) {
      apiData.push({
        x: (d.TimeAbs_t + utcOffsetSeconds) * 1000,
        y: d.Wind_v * 3.6, // Convert m/s to km/h
      });
    }
  }

  if (probeData.length === 0 && apiData.length === 0) {
    return (
      <div className="cr-flex cr-h-96 cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500">
        No wind speed data available
      </div>
    );
  }

  // Sort by time
  probeData.sort((a, b) => a.x - b.x);

  // Calculate scaling factor for Weather API data
  let apiScalingFactor = 1;
  let apiLabel = 'Weather API Wind Speed';

  if (probeData.length > 0 && apiData.length > 0) {
    // Calculate average probe wind speed and time
    const avgProbeSpeed = probeData.reduce((sum, d) => sum + d.y, 0) / probeData.length;
    const avgProbeTime = probeData.reduce((sum, d) => sum + d.x, 0) / probeData.length;

    // Find nearest Weather API point to average probe time
    let nearestApiPoint = apiData[0];
    let minTimeDiff = Math.abs(apiData[0].x - avgProbeTime);

    for (const point of apiData) {
      const timeDiff = Math.abs(point.x - avgProbeTime);
      if (timeDiff < minTimeDiff) {
        minTimeDiff = timeDiff;
        nearestApiPoint = point;
      }
    }

    // Calculate and round scaling factor
    const rawScalingFactor = nearestApiPoint.y / avgProbeSpeed;
    apiScalingFactor = Math.round(rawScalingFactor);

    // Only apply scaling if factor is valid and not 1
    if (apiScalingFactor > 0 && apiScalingFactor !== 1) {
      // Scale all API data points
      apiData.forEach(d => {
        d.y = d.y / apiScalingFactor;
      });
      apiLabel = `Weather API Wind Speed / ${apiScalingFactor}`;
    }
  }

  // Calculate time range: 90 minutes before first probe run and 90 minutes after last probe run
  const minTime =
    probeData.length > 0 ? probeData[0].x : apiData.length > 0 ? apiData[0].x : Date.now();
  const maxTime =
    probeData.length > 0
      ? probeData[probeData.length - 1].x
      : apiData.length > 0
        ? apiData[apiData.length - 1].x
        : Date.now();
  const ninetyMinutesMs = 90 * 60 * 1000;

  const seriesData: any[] = [];
  const colorArray: string[] = [];

  if (probeData.length > 0) {
    seriesData.push({
      name: 'Probe Wind Speed',
      data: probeData,
    });
    colorArray.push('#10b981'); // Green
  }

  if (apiData.length > 0) {
    seriesData.push({
      name: apiLabel,
      data: apiData,
    });
    colorArray.push('#3b82f6'); // Blue
  }

  const options: ApexOptions = {
    chart: {
      type: 'line',
      height: 384,
      zoom: { enabled: true, allowMouseWheelZoom: false},
      toolbar: { show: true },
      animations: { enabled: false },
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    markers: {
      size: 0,
    },
    colors: colorArray,
    xaxis: {
      type: 'datetime',
      min: minTime - ninetyMinutesMs,
      max: maxTime + ninetyMinutesMs,
      labels: {
        datetimeUTC: true,
        format: 'HH:mm',
      },
      title: {
        text: 'Time of Day',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Wind Speed (km/h)',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        formatter: (val: number) => val.toFixed(1),
      },
    },
    grid: {
      borderColor: '#e5e7eb',
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      fontWeight: 500,
    },
    tooltip: {
      x: {
        format: 'HH:mm:ss',
      },
      y: {
        formatter: (val: number) => `${val.toFixed(2)} km/h`,
      },
    },
    title: {
      text: 'Wind Speed',
      align: 'center',
    },
  };

  return (
    <ChartCard className="cr-h-96" padding>
      <Chart options={options} series={seriesData} type="line" height="100%" />
    </ChartCard>
  );
}

function WindDirectionPlot({ configs, utcOffsetSeconds, weatherControllerDict }: {
  configs: any[];
  utcOffsetSeconds: number;
  weatherControllerDict: any;
}): ReactElement {
  const probeDirectionData: any[] = [];

  // Collect wind direction data from all runs (probe)
  for (const config of configs) {
    for (const run of config.runs) {
      const avgWindDirection = run.average?.WindDirAbs_AG3?.[0];
      const runTime = run.average?.GatesStartTs_tG3?.[0];

      if (avgWindDirection == null || runTime == null) {
        continue;
      }

      probeDirectionData.push({
        x: (runTime + utcOffsetSeconds) * 1000, // Convert to milliseconds
        y: avgWindDirection,
      });
    }
  }

  // Collect Weather API wind direction data
  const apiDirectionData: any[] = [];
  const weatherApiSource = weatherControllerDict?.weather_api;
  if (weatherApiSource?.weather_data) {
    const validData = weatherApiSource.weather_data
      .filter((d: any) => d.WindDir_A !== null && d.TimeAbs_t !== null)
      .sort((a: any, b: any) => a.TimeAbs_t - b.TimeAbs_t);

    for (const d of validData) {
      apiDirectionData.push({
        x: (d.TimeAbs_t + utcOffsetSeconds) * 1000,
        y: d.WindDir_A,
      });
    }
  }

  if (probeDirectionData.length === 0 && apiDirectionData.length === 0) {
    return (
      <div className="cr-flex cr-h-96 cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500">
        No wind direction data available
      </div>
    );
  }

  // Sort by time
  probeDirectionData.sort((a, b) => a.x - b.x);

  // Calculate time range: 90 minutes before first probe run and 90 minutes after last probe run
  const minTime =
    probeDirectionData.length > 0
      ? probeDirectionData[0].x
      : apiDirectionData.length > 0
        ? apiDirectionData[0].x
        : Date.now();
  const maxTime =
    probeDirectionData.length > 0
      ? probeDirectionData[probeDirectionData.length - 1].x
      : apiDirectionData.length > 0
        ? apiDirectionData[apiDirectionData.length - 1].x
        : Date.now();
  const ninetyMinutesMs = 90 * 60 * 1000;

  const seriesData: any[] = [];
  const colorArray: string[] = [];

  if (probeDirectionData.length > 0) {
    seriesData.push({
      name: 'Probe Wind Direction',
      data: probeDirectionData,
    });
    colorArray.push('#10b981'); // Green
  }

  if (apiDirectionData.length > 0) {
    seriesData.push({
      name: 'Weather API Wind Direction',
      data: apiDirectionData,
    });
    colorArray.push('#3b82f6'); // Blue
  }

  const options: ApexOptions = {
    chart: {
      type: 'scatter',
      height: 384,
      zoom: { enabled: true, allowMouseWheelZoom: false},
      toolbar: { show: true },
      animations: { enabled: false },
    },
    markers: {
      size: 6,
      strokeColors: '#fff',
      strokeWidth: 2,
    },
    colors: colorArray,
    xaxis: {
      type: 'datetime',
      min: minTime - ninetyMinutesMs,
      max: maxTime + ninetyMinutesMs,
      labels: {
        datetimeUTC: true,
        format: 'HH:mm',
      },
      title: {
        text: 'Time of Day',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 360,
      tickAmount: 8,
      title: {
        text: 'Wind Direction (degrees)',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        formatter: (val: number) => val.toFixed(0),
      },
    },
    grid: {
      borderColor: '#e5e7eb',
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      fontWeight: 500,
    },
    tooltip: {
      x: {
        format: 'HH:mm:ss',
      },
      y: {
        formatter: (val: number) => `${val.toFixed(1)}°`,
      },
    },
    title: {
      text: 'Wind Direction',
      align: 'center',
    },
  };

  return (
    <ChartCard className="cr-h-96" padding>
      <Chart options={options} series={seriesData} type="scatter" height="100%" />
    </ChartCard>
  );
}

function PowerDeltaBarChart({
  configs,
  configMap,
  speedKPH,
}: {
  configs: any[];
  configMap: Map<string, any>;
  speedKPH: number;
}): ReactElement {
  const powerSpeedKey = 'RiderPowerFromSpeed_PG3';

  // Build chart data: group configs by baseline and calculate power delta
  const chartData = useMemo(() => {
    const categories: string[][] = [];
    const seriesData: number[] = [];
    const colors: string[] = [];

    // Helper to wrap text into lines of max ~15 chars
    const wrapText = (text: string, maxChars: number = 10): string[] => {
      if (!text) return [];
      const words = text.split(' ');
      const lines: string[] = [];
      let currentLine = '';

      for (const word of words) {
        if (currentLine.length + word.length + 1 <= maxChars) {
          currentLine = currentLine ? `${currentLine} ${word}` : word;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      }
      if (currentLine) lines.push(currentLine);
      return lines;
    };

    // Process each config
    for (const config of configs) {
      const baselineId = config.baseline_id || '-1';
      const baseline = baselineId === '-1' ? null : configMap.get(baselineId);
      const baselinePower = baseline?.average?.[powerSpeedKey]?.[0] || null;
      const power = config.average?.[powerSpeedKey]?.[0];

      if (power != null) {
        const powerDiff = baselinePower != null ? power - baselinePower : 0;
        const letter = config.letter || `Config ${config.id.slice(0, 4)}`;
        const description = config.description || '';
        // Create multi-line label array for ApexCharts
        const labelLines = [letter, ...wrapText(description)];
        categories.push(labelLines);
        seriesData.push(powerDiff);
        // Color based on sign: blue for negative (good), gray for positive (bad)
        colors.push(powerDiff > 0 ? '#545454' : '#1879B9');
      }
    }

    return { categories, seriesData, colors };
  }, [configs, configMap]);

  if (chartData.seriesData.length === 0) {
    return (
      <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-gray-500">
        <p>No power delta data available</p>
      </div>
    );
  }

  // Calculate y-axis range
  const minVal = Math.min(...chartData.seriesData, 0);
  const maxVal = Math.max(...chartData.seriesData, 0);
  const range = maxVal - minVal || 10;
  const padding = range * 0.2;

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 400,
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      fontFamily: 'Poppins',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        distributed: true,
        dataLabels: {
          position: 'top',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val: number) {
        return val.toFixed(0) + 'W';
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ['#333'],
      },
    },
    stroke: {
      show: true,
      width: 1,
      colors: ['#333'],
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        rotate: 0,
        trim: false,
        hideOverlappingLabels: false,
        maxHeight: 120,
        style: {
          fontSize: '10px',
          fontFamily: 'Poppins',
          fontWeight: 600,
        },
      },
      axisBorder: {
        show: true,
        color: '#333',
      },
    },
    yaxis: {
      title: {
        text: 'Power Delta (Watts)',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          fontFamily: 'Poppins',
        },
      },
      min: minVal - padding,
      max: maxVal + padding,
      labels: {
        formatter: function (val: number) {
          return val.toFixed(0);
        },
      },
    },
    colors: chartData.colors,
    legend: {
      show: false,
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val.toFixed(1) + ' W';
        },
      },
    },
    annotations: {
      yaxis: [
        {
          y: 0,
          borderColor: 'gray',
          borderWidth: 2,
          strokeDashArray: 0,
        },
      ],
    },
    title: {
      text: `Power Delta by Configuration at ${speedKPH.toFixed(0)} kph`,
      align: 'center',
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        fontFamily: 'Poppins',
      },
    },
    subtitle: {
      text: 'Relative to baseline configuration (negative = power savings)',
      align: 'center',
      style: {
        fontSize: '12px',
        fontFamily: 'Poppins',
        color: '#666',
      },
    },
  };

  const series = [
    {
      name: 'Power Delta',
      data: chartData.seriesData,
    },
  ];

  return (
    <ChartCard className="cr-h-96" padding>
      <Chart options={options} series={series} type="bar" height="100%" />
    </ChartCard>
  );
}

function PrintHeader({
  sessionData,
  ridersById,
}: {
  sessionData: any;
  ridersById: Map<string, any> | undefined;
}): ReactElement {
  const route = sessionData?.gates?.route;
  const rider = sessionData?.rider_id && ridersById?.get(sessionData.rider_id);
  const riderName = rider ? getDisplayName(rider) : '';
  const description = sessionData?.description || '';

  return (
    <div className="print-only print-header">
      <div className="print-header-title">AERO REPORT</div>
      <p>
        <strong>Date:</strong> {sessionData?.date || 'N/A'}
        {riderName && (<> &nbsp;|&nbsp; <strong>Rider:</strong> {riderName}</>)}
        {route?.route_address?.District && (
          <> &nbsp;|&nbsp; <strong>Location:</strong> {route.route_address.District}
            {route.route_address.Locality && route.route_address.Locality !== route.route_address.District
              ? `, ${route.route_address.Locality}`
              : ''}
          </>
        )}
      </p>
      {description && <p className="print-description"><strong>Description:</strong> {description}</p>}
    </div>
  );
}

import {
  PrintColumn,
  PrintDataTable,
  buildPrintDataRows,
  getStandardCellValue,
} from '../../../../components/PrintDataTable';

// Page 1: Run info, CdA, and Power components
const PRINT_DATA_COLUMNS_AERO_PAGE1: PrintColumn[] = [
  { displayName: 'Run Number', internalName: 'run_number', source: 'run', places: 0 },
  { displayName: 'Start Time', internalName: 'run_ts_tz', source: 'run', places: -1 },
  { displayName: 'Description', internalName: 'description', source: 'config', places: -1, dividerAfter: true },
  { displayName: 'CdA 5°XW Normalised Full<br />m²', internalName: 'CdAN_xG3', source: 'average', places: 3 },
  { displayName: 'CdA Raw<br />m²', internalName: 'CdA_xG3', source: 'average', places: 3, dividerAfter: true },
  { displayName: 'Alpha<br />W', internalName: 'Alpha_PG3', source: 'average', places: 0 },
  { displayName: 'Aero<br />W', internalName: 'Aero_PG3', source: 'average', places: 0 },
  { displayName: 'Grav<br />W', internalName: 'Gravitational_PG3', source: 'average', places: 0 },
  { displayName: 'Kinetic<br />W', internalName: 'Kinetic_PG3', source: 'average', places: 0 },
  { displayName: 'Rolling<br />W', internalName: 'RollingResistance_PG5', source: 'average', places: 0 },
  { displayName: 'Power<br />W', internalName: 'Rider_PG3', source: 'average', places: 0 },
];

// Page 2: Run number (repeated), Duration, Speed, Position, and Calibration
const PRINT_DATA_COLUMNS_AERO_PAGE2: PrintColumn[] = [
  { displayName: 'Run Number', internalName: 'run_number', source: 'run', places: 0, dividerAfter: true },
  { displayName: 'Distance<br />m', internalName: 'WhlLdistDiff_sG3', source: 'average', places: 1 },
  { displayName: 'Duration<br />s', internalName: 'TimeAbsDiff_tG3', source: 'average', places: 1 },
  { displayName: 'Speed<br />m/s', internalName: 'WhlLspd_vG3', source: 'average', places: 2 },
  { displayName: 'Air Speed<br />m/s', internalName: 'AirSpeed_vG3', source: 'average', places: 2, dividerAfter: true },
  { displayName: 'Yaw (abs)<br />deg.', internalName: 'AirYawAbs_AG3', source: 'average', places: 1 },
  { displayName: 'Yaw<br />deg.', internalName: 'AirYaw_AG3', source: 'average', places: 1 },
  { displayName: 'Head<br />cm', internalName: 'BodyPosHead_sG3_cm', source: 'average', places: 1 },
  { displayName: 'Chest<br />cm', internalName: 'BodyPosChest_sG3_cm', source: 'average', places: 1, dividerAfter: true },
  { displayName: 'CpCorr', internalName: 'cp_corr', source: 'config', places: 3 },
  { displayName: 'Wheel Circumference<br />m', internalName: 'wheel_circumference', source: 'config', places: 2 },
  { displayName: 'Yaw Offset<br />°', internalName: 'yaw_offset', source: 'config', places: 2 },
  { displayName: 'Combined Mass', internalName: 'combined_mass', source: 'config', places: 2 },
  { displayName: 'Power Meter Slope', internalName: 'power_meter_slope', source: 'config', places: 3 },
  { displayName: 'Crr', internalName: 'crr', source: 'config', places: 5 },
  { displayName: 'Crr at Test Temp', internalName: 'CrrInput_xG5', source: 'average', places: 5 },
];

function AeroPrintDataTable({ configs }: { configs: any[] }): ReactElement {
  const rows = buildPrintDataRows(configs);

  return (
    <PrintDataTable
      rows={rows}
      columnsPage1={PRINT_DATA_COLUMNS_AERO_PAGE1}
      columnsPage2={PRINT_DATA_COLUMNS_AERO_PAGE2}
      titlePage1="Detailed Run Data - CdA & Power"
      titlePage2="Detailed Run Data - Speed, Position & Calibration"
      getCellValue={getStandardCellValue}
      className="print-only print-data-table"
    />
  );
}

export default function SessionCharts(): ReactElement {
  const { session } = useOutletContext<{
    session: any;
  }>();
  const sessionData = session;
  const { data: ridersById } = useRidersById();

  const [normalizationType, setNormalizationType] = useState('standard');
  const [isCourseCollapsed, setIsCourseCollapsed] = useState(() => {
    const stored = localStorage.getItem('sessionCharts.isCourseCollapsed');
    return stored === 'true';
  });
  const [isWeatherCollapsed, setIsWeatherCollapsed] = useState(() => {
    const stored = localStorage.getItem('sessionCharts.isWeatherCollapsed');
    return stored === 'true';
  });
  const [isPerformanceCollapsed, setIsPerformanceCollapsed] = useState(() => {
    const stored = localStorage.getItem('sessionCharts.isPerformanceCollapsed');
    return stored === 'true';
  });
  const [isExportPreparing, setIsExportPreparing] = useState(false);
  const [exportFlash, setExportFlash] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const printContainerRef = useRef<HTMLDivElement>(null);

  const handleExportHtml = async (mode: 'full' | 'concise') => {
    setShowExportPopup(false);

    // Trigger flash animation
    setExportFlash(true);
    setTimeout(() => setExportFlash(false), 300);

    setIsExportPreparing(true);

    // Allow React to render the spinner before starting export
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Build title in "Rider - Date" format to match browser tab
    const riderName = ridersById?.get(sessionData?.rider_id)?.nickname || 'Unknown Rider';
    let formattedDate = '';
    if (sessionData?.date) {
      const parsedDate = parse(sessionData.date, 'yyyy-MM-dd', new Date());
      if (isValid(parsedDate)) {
        formattedDate = parsedDate.toLocaleDateString();
      }
    }
    const exportTitle = formattedDate ? `${riderName} - ${formattedDate}` : riderName;

    // In concise mode, hide the normalisation section before export
    const container = printContainerRef.current;
    const conciseElements = container?.querySelectorAll('[data-concise-hide]');
    if (mode === 'concise') {
      conciseElements?.forEach(el => (el as HTMLElement).style.display = 'none');
    }

    await executeExportHtml(printContainerRef, {
      waitTime: 10000,
      title: exportTitle,
      onAfterExport: () => {
        conciseElements?.forEach(el => (el as HTMLElement).style.display = '');
        setIsExportPreparing(false);
      },
      leafletWidthPercent: 65,
    });
  };

  if (!sessionData) return <div>Loading Data...</div>;
  const plotData = sessionData.plot_data;
  const configs = sessionData.configs.filter((config: any) =>
    config.runs.some((run: any) => run.traverse?.[CDA_KEY]?.length > 0),
  );
  const configsLength = configs?.length;
  const lengthString = NUMBERS[configsLength] || configsLength;
  if (configsLength === 0) return <div>No Configs were found.</div>;
  const speedMS = session.riders?.speed_ref || 11.1111;
  const speedKPH = speedMS * 3.6;
  const powerSpeed: string = speedKPH.toFixed(0);
  const displayNames = [
    'Config',
    'Description',
    'CdA (Normalised)',
    'CdA Delta',
    `Power in Watts at ${powerSpeed} kph`,
    'Power Delta',
  ];
  const configMap = new Map();
  const configGroups = new Map();
  for (const config of configs) {
    const baselineId = config.baseline_id || '-1';
    const configGroup = configGroups.get(baselineId) || [];
    configGroup.push(config);
    configGroups.set(baselineId, configGroup);
    configMap.set(config.id, config);
  }
  const route = sessionData.gates?.route;
  const gates = sessionData.gates;

  // Get meteorological source selection
  const selectionMap: Record<string, string> = {
    weather_api: 'Weather Api',
    probe: 'Probe',
    probe_hybrid: 'Probe-API',
    local_station: 'Local Weather Station',
    manual: 'Manual',
    bike_computer: 'Bike Computer',
  };
  const meteorologicalSource = sessionData?.weather_controller_dict?.selection
    ? selectionMap[sessionData.weather_controller_dict.selection] || 'Weather Api'
    : 'Weather Api';

  return (
    <div ref={printContainerRef} className="overflow-auto">
      {/* Print logo - appears on every printed page */}
      <div className="print-logo">
        <img src={streamlinesLogo} alt="Streamlines" />
      </div>

      <div className="cr-flex cr-items-center cr-justify-between cr-mb-4 cr-mt-4 screen-only">
        <h1 className="cr-text-base">
          This report is for an outdoor aerodynamic rider test from {sessionData.date}
          {route?.route_address?.District &&
            ` near ${route.route_address.District}${
              route.route_address.Locality && route.route_address.Locality !== route.route_address.District
                ? `, ${route.route_address.Locality}`
                : ''
            }`}
        </h1>
        <button
          onClick={() => setShowExportPopup(true)}
          className={`cr-flex cr-items-center cr-gap-2 cr-px-4 cr-py-2 cr-text-sm cr-font-semibold
            cr-rounded-md cr-transition-all cr-duration-200 focus:cr-outline-none
            focus:cr-ring-2 focus:cr-ring-offset-2 print:cr-hidden ${
            exportFlash
              ? 'cr-bg-green-400 cr-scale-105 cr-shadow-lg'
              : 'cr-bg-green-600 hover:cr-bg-green-700 cr-text-white focus:cr-ring-green-500'
          }`}
          style={exportFlash ? { boxShadow: '0 0 20px rgba(34, 197, 94, 0.6)' } : undefined}
        >
          <HiOutlineDocumentArrowDown className="cr-size-5" />
          <span>Export</span>
        </button>
      </div>

      {/* Export Preparing Spinner */}
      {isExportPreparing && createPortal(
        <div
          className="screen-only"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTop: '4px solid #22c55e',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <p style={{ color: 'white', marginTop: '16px', fontSize: '18px', fontWeight: 500 }}>
            Exporting HTML...
          </p>
          <p style={{ color: '#9ca3af', marginTop: '8px', fontSize: '14px', fontWeight: 400 }}>
            For best results with export files, please remain on page until export is finished.
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>,
        document.body
      )}

      {/* Export Mode Popup */}
      {showExportPopup && createPortal(
        <div
          className="screen-only"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
          }}
          onClick={() => setShowExportPopup(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              minWidth: '340px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
              Export Aero Report
            </h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#6b7280' }}>
              Choose export format
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => handleExportHtml('full')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  backgroundColor: '#16a34a',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Full
              </button>
              <button
                onClick={() => handleExportHtml('concise')}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  backgroundColor: '#2563eb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Concise
              </button>
            </div>
            <button
              onClick={() => setShowExportPopup(false)}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '8px',
                fontSize: '14px',
                color: '#6b7280',
                backgroundColor: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}

      <PrintHeader sessionData={sessionData} ridersById={ridersById} />

      {/* Course Section */}
      <div className="cr-my-4">
        <button
          onClick={() => {
            const newValue = !isCourseCollapsed;
            setIsCourseCollapsed(newValue);
            localStorage.setItem('sessionCharts.isCourseCollapsed', String(newValue));
          }}
          className="font-montserrat cr-flex cr-items-center cr-gap-2 cr-text-2xl-tight cr-font-bold cr-mb-4 cr-cursor-pointer cr-hover:text-blue-600 cr-transition-colors screen-only"
        >
          <span>Course</span>
          {isCourseCollapsed ? <HiChevronDown className="cr-size-5 cr-stroke-2" /> : <HiChevronUp className="cr-size-5 cr-stroke-2" />}
        </button>
        <div className="print-only print-section-header">Course</div>
        <div className={`${isCourseCollapsed ? 'cr-hidden' : ''} print-force-show`}>
          {route ? (
            <>
              <div className="cr-grid cr-grid-cols-2 cr-gap-4">
                <ReportMap gate={gates} route={route} />
                <ElevationPlot route={route} gates={gates} />
              </div>
              {gates?.gates_lat && gates?.gates_lon && (
                <div className="cr-mt-8 cr-pt-4 cr-flex cr-gap-8 cr-text-lg">
                  <div>
                    <span className="cr-font-medium">Distance between gates: </span>
                    <span>{gates.route_distance?.toFixed(0) || 'N/A'} m</span>
                  </div>
                  <div>
                    <span className="cr-font-medium">Elevation difference (A to B): </span>
                    <span>{calculateElevationDifference(route, gates).toFixed(1)} m</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>No route data available to display map</div>
          )}
        </div>
      </div>

      {/* Weather Section */}
      <div className="cr-mt-8 cr-mb-4">
        <button
          onClick={() => {
            const newValue = !isWeatherCollapsed;
            setIsWeatherCollapsed(newValue);
            localStorage.setItem('sessionCharts.isWeatherCollapsed', String(newValue));
          }}
          className="font-montserrat cr-flex cr-items-center cr-gap-2 cr-text-2xl-tight cr-font-bold cr-mb-4 cr-cursor-pointer cr-hover:text-blue-600 cr-transition-colors screen-only"
        >
          <span>Weather</span>
          {isWeatherCollapsed ? <HiChevronDown className="cr-size-5 cr-stroke-2" /> : <HiChevronUp className="cr-size-5 cr-stroke-2" />}
        </button>
        <div className="print-only print-section-header">Weather</div>
        <div className={`${isWeatherCollapsed ? 'cr-hidden' : ''} print-force-show`}>
          <div className="cr-grid cr-grid-cols-3 cr-gap-4">
            <WeatherTemperaturePlot
              weatherControllerDict={sessionData?.weather_controller_dict || null}
              selectedSource={meteorologicalSource}
              utcOffsetSeconds={Number(sessionData?.utc_offset ?? 0)}
            />
            <WindSpeedPlot
              configs={configs}
              utcOffsetSeconds={Number(sessionData?.utc_offset ?? 0)}
              weatherControllerDict={sessionData?.weather_controller_dict || null}
            />
            <WindDirectionPlot
              configs={configs}
              utcOffsetSeconds={Number(sessionData?.utc_offset ?? 0)}
              weatherControllerDict={sessionData?.weather_controller_dict || null}
            />
          </div>
        </div>
      </div>

      {/* Results Section */}
      <div className="cr-mt-8 print-page-break-before">
        <div className="print-only print-section-header">Results</div>
        <div className="font-montserrat cr-my-4 cr-text-2xl-tight cr-font-bold screen-only">Results</div>
      <div className="cr-text-base cr-mb-6">{lengthString} configurations were tested</div>
      {[...configGroups.keys()].map((baselineId: any) => (
        <ConfigGroup
          key={baselineId}
          baselineId={baselineId}
          configGroups={configGroups}
          configMap={configMap}
          displayNames={displayNames}
        />
      ))}

      {/* Performance Sub-section */}
      <div className="cr-my-4 print-page-break-before">
        <button
          onClick={() => {
            const newValue = !isPerformanceCollapsed;
            setIsPerformanceCollapsed(newValue);
            localStorage.setItem('sessionCharts.isPerformanceCollapsed', String(newValue));
          }}
          className="font-montserrat cr-flex cr-items-center cr-gap-2 cr-text-xl cr-font-semibold cr-mb-4 cr-cursor-pointer cr-hover:text-blue-600 cr-transition-colors screen-only"
        >
          <span>Performance</span>
          {isPerformanceCollapsed ? <HiChevronDown className="cr-size-5 cr-stroke-2" /> : <HiChevronUp className="cr-size-5 cr-stroke-2" />}
        </button>
        <div className="print-only print-section-header">Performance</div>
        <div className={`${isPerformanceCollapsed ? 'cr-hidden' : ''} print-force-show print-chart-padding`}>
          <PowerDeltaBarChart
            configs={configs}
            configMap={configMap}
            speedKPH={speedKPH}
          />
        </div>
      </div>

      {/* Data Quality & Normalisation */}
      <div>
        <div className="print-page-break-before">
          <AveragePlots configs={configs} session={sessionData} />
        </div>

        {/* Data Normalisation Section */}
        <div data-concise-hide>
        <div className="print-page-break-before">
          <div className="font-montserrat cr-mt-8 cr-mb-4 cr-font-semibold cr-text-xl screen-only">Data Normalisation</div>
          <div className="print-only print-section-header">Data Normalisation</div>
        </div>
        <div className="print-only cr-text-lg cr-mb-4">
          <strong>Normalisation Type:</strong> {normalizationType.charAt(0).toUpperCase() + normalizationType.slice(1)}
        </div>

        {/* Normalisation Type Radio Buttons */}
        <div className="cr-my-4 cr-text-lg cr-text-blue-900 screen-only">
          <span className="cr-mr-4 cr-font-medium">Normalisation Type:</span>
          <label className="cr-mr-4 cr-cursor-pointer">
            <input
              type="radio"
              name="normalizationType"
              value="standard"
              checked={normalizationType === 'standard'}
              onChange={() => setNormalizationType('standard')}
              className="cr-w-4 cr-h-4"
            />{' '}
            Standard
          </label>
          <label className="cr-mr-4 cr-cursor-pointer">
            <input
              type="radio"
              name="normalizationType"
              value="flattened"
              checked={normalizationType === 'flattened'}
              onChange={() => setNormalizationType('flattened')}
              className="cr-w-4 cr-h-4"
            />{' '}
            Flattened
          </label>
          <label className="cr-cursor-pointer">
            <input
              type="radio"
              name="normalizationType"
              value="raw"
              checked={normalizationType === 'raw'}
              onChange={() => setNormalizationType('raw')}
              className="cr-w-4 cr-h-4"
            />{' '}
            Raw
          </label>
        </div>
        <div className="cr-grid cr-grid-cols-2 cr-gap-4" key={normalizationType}>
          {['yaw_plot', 'speed_plot', 'chest_plot', 'head_plot'].map((plotType: string) => (
            <LinePlot
              key={`${normalizationType}_${plotType}`}
              configs={configs}
              plotData={plotData?.[plotType]?.[normalizationType] || null}
              plotType={plotType}
              session={sessionData}
            />
          ))}
        </div>
        </div>
      </div>
      </div>

      {/* Print-only detailed data table */}
      <AeroPrintDataTable configs={configs} />
    </div>
  );
}
